import { ReactNode } from "react";

import { useManualPreset } from "@vericus/cadmus-manual";
import { colors, Icon, Spacer, Text } from "@vericus/cadmus-ui";

import { UseFormReturnType } from "@mantine/form";

import { inferLiveExamEndDate } from "@/features/settings";
import {
  AssessmentType,
  ExamTiming,
  RequirementsFragment,
  TaskFormat,
  TaskKind,
} from "@/generated/graphql";
import { useFeatureFlagsFragment } from "@/graphql/institution-selectors";

import { AssessmentNoun } from "../../assessment-utils";
import * as Req from "../requirements-rows";
import { SectionProps } from "./shared";

interface ExamRequirementSection extends SectionProps {
  validationErrors?: RequirementViewErrors;
  format?: TaskFormat;
  setExamStart?: VoidFunction;
  kind?: TaskKind;
  assessmentNoun?: AssessmentNoun;
}

/**
 * Render the public section of exam requirements.
 *
 * If `enableAutoSubmission` is true, then we show this setting to the students.
 */
export function VisibleExamRequirements({
  reqs,
  diff,
  validationErrors,
  goToEditSettings,
  setExamStart,
  format,
  kind,
  assessmentNoun,
}: ExamRequirementSection) {
  const features = useFeatureFlagsFragment();
  const ldbFeatureEnabled = features?.respondusLdb === true;
  const schoolyearFeatureEnabled = features?.schoolyear === true;

  // Check if the LDB feature is enabled in the current assessment
  const isLdbEnabled =
    reqs.assessmentType === AssessmentType.Exam &&
    ldbFeatureEnabled &&
    reqs.enableExamLdb === true;

  const isSchoolyearEnabled =
    reqs.assessmentType === AssessmentType.Exam &&
    schoolyearFeatureEnabled &&
    reqs.examSchoolyearEnabled === true;

  const openManualPreset = useManualPreset();
  return (
    <div data-testid="ExamRequirementsContainer">
      <Req.ExamStart
        examType={reqs.examTiming}
        examStart={reqs.examStartDate}
        errorMessage={validationErrors?.examStartDate}
        hasChanged={diff?.examStartDate}
        setExamStart={setExamStart}
        assessmentNoun={assessmentNoun}
      />
      {!reqs.noExamReadingTime &&
        (reqs.examReadingTime != null ||
          validationErrors?.examReadingTime != null) && (
          <Req.ReadingTime
            readingTime={reqs.examReadingTime}
            errorMessage={validationErrors?.examReadingTime}
            hasChanged={diff?.examReadingTime}
          />
        )}

      {(reqs.examWritingTime != null ||
        validationErrors?.examWritingTime != null) && (
        <Req.WritingTime
          writingTime={reqs.examWritingTime}
          isWindowExam={reqs.examTiming === ExamTiming.Window}
          errorMessage={validationErrors?.examWritingTime}
          hasChanged={diff?.examWritingTime}
          kind={kind}
        />
      )}

      {reqs.examTiming === ExamTiming.Live &&
        reqs.examStartDate != null &&
        reqs.examWritingTime != null && (
          <Req.ExamEnd
            examType={reqs.examTiming}
            examEnd={inferLiveExamEndDate(reqs)}
            hasChanged={
              diff &&
              (diff.examStartDate ||
                diff.examReadingTime ||
                diff.examWritingTime)
            }
            assessmentNoun={assessmentNoun}
          />
        )}

      {reqs.examTiming === ExamTiming.Window && (
        <Req.ExamEnd
          examType={reqs.examTiming}
          examEnd={reqs.examEndDate}
          errorMessage={validationErrors?.examEndDate}
          hasChanged={diff?.examEndDate}
          setExamEnd={goToEditSettings}
          assessmentNoun={assessmentNoun}
        />
      )}

      {reqs.enableExamAutoSubmission && (
        <Req.ExamAutosubmission
          isAutosubmisisonEnabled={reqs.enableExamAutoSubmission}
          lateSubmissionMinsTimeLimit={reqs.lateSubmissionTimeLimit}
          hasChanged={diff?.enableExamAutoSubmission}
          assessmentNoun={assessmentNoun}
        />
      )}

      <Req.ExamResubmissions
        submitAttemptLimit={reqs.submitAttemptLimit}
        hasChanged={diff?.submitAttemptLimit}
      />

      {!reqs.noWeight && reqs.weight != null && (
        <Req.Weight weight={reqs.weight} hasChanged={diff?.weight} />
      )}
      {format === TaskFormat.Classic && (
        <>
          {reqs.wordLimit !== null && !reqs.noLimit && (
            <Req.WordLimit
              isExam={true}
              wordLimit={reqs.wordLimit}
              countReferences={reqs.countReferences}
              countFootnotes={reqs.countFootnotes}
              hasChanged={diff && diff.wordLimit}
              hideIncludeReferences
            />
          )}

          {reqs.referencingStyle !== null && !reqs.noReferencing && (
            <Req.ReferencingStyle
              referencingStyle={reqs.referencingStyle}
              hasChanged={diff && diff.referencingStyle}
            />
          )}
        </>
      )}
      {isLdbEnabled || isSchoolyearEnabled ? (
        <>
          {isLdbEnabled && (
            <Req.ExamLDB
              ldbEnabled={isLdbEnabled}
              hasChanged={diff && diff.enableExamLdb}
              assessmentNoun={assessmentNoun}
            />
          )}
          {isSchoolyearEnabled && (
            <Req.ExamSchoolyear
              examSchoolyearEnabled={isSchoolyearEnabled}
              hasChanged={diff && diff.examSchoolyearEnabled}
              assessmentNoun={assessmentNoun}
            />
          )}
        </>
      ) : (
        <>
          {reqs.allowGenerativeAi !== null && (
            <Req.GenerativeAi
              allowGenerativeAi={reqs.allowGenerativeAi}
              hasChanged={diff?.allowGenerativeAi}
              onOpenManualPreset={openManualPreset}
            />
          )}

          <Req.AcademicIntegrity onOpenManualPreset={openManualPreset} />
        </>
      )}
    </div>
  );
}

/**
 * Render the private section of assignment requirements (invisible to students)
 *
 * If `enableAutoSubmission` is false, then we don't show this setting to
 * the students. The idea is that we want to discourage the students from
 * working on the exam to the absolute end (beyond the original exam end date).
 */
export function HiddenExamRequirements({
  reqs,
  diff,
  validationErrors,
  goToEditSettings,
  assessmentNoun,
}: ExamRequirementSection) {
  return (
    <>
      <Spacer spacing={27} />
      <Text kind="headingSix" style={{ color: colors.grey600 }} as="div">
        <Icon iconName="Show" iconColor="grey500" /> Additional settings not
        visible to students
      </Text>
      <Spacer spacing={18} />

      {/* We show either the CTA or the value if the setting for feedback has been turned on */}
      {!reqs.noExamFeedbackDate && (
        <Req.ExamReturnDate
          examReturnDate={reqs.examFeedbackDate}
          hasChanged={diff?.examFeedbackDate}
          setExamFeedbackDate={goToEditSettings}
        />
      )}

      {!reqs.enableExamAutoSubmission && (
        <Req.ExamAutosubmission
          isAutosubmisisonEnabled={reqs.enableExamAutoSubmission}
          lateSubmissionMinsTimeLimit={reqs.lateSubmissionTimeLimit}
          hasChanged={diff?.enableExamAutoSubmission}
          assessmentNoun={assessmentNoun}
        />
      )}

      <Req.GradingService
        gradingService={reqs.gradingService}
        hasChanged={diff?.gradingService}
      />
      <Req.AnonymousMarking
        anonymousMarking={reqs.anonymousMarking}
        hasChanged={diff?.anonymousMarking}
      />
      {reqs.enableSubmissionPdf && (
        <Req.PdfSubmission hasChanged={diff?.enableSubmissionPdf} />
      )}
      {(reqs.maxGrade != null || validationErrors?.maxGrade != null) &&
        (reqs.gradingService === "turnitin" ||
          reqs.gradingService === "cadmusgrader") && (
          <Req.MaxGrade
            maxGrade={reqs.maxGrade}
            errorMessage={validationErrors?.maxGrade}
            hasChanged={diff?.maxGrade}
          />
        )}
      {reqs.gradingService === "cadmusgrader" && (
        <Req.RubricsDisplay
          rubricsDisplay={reqs.rubricDisplayDisabled !== true}
          hasChanged={diff?.rubricDisplayDisabled}
        />
      )}
      <Req.SpellCheck
        disableSpellCheck={reqs.disableSpellCheck}
        hasChanged={diff?.disableSpellCheck}
      />
    </>
  );
}

type ValidationError = ReactNode;

type RequirementViewFieldsWithErrors =
  | "examStartDate"
  | "examEndDate"
  | "maxGrade"
  | "examReadingTime"
  | "examWritingTime";

export type RequirementViewErrors = Partial<
  Record<RequirementViewFieldsWithErrors, ValidationError>
>;

export const useExamRequirementErrors = (
  settingsForm: UseFormReturnType<RequirementsFragment>
): RequirementViewErrors => {
  const {
    examStartDate,
    examEndDate,
    maxGrade,
    examReadingTime,
    examWritingTime,
  } = settingsForm.errors;
  return {
    examStartDate:
      examStartDate != null ? "Please add exam start date and time" : undefined,
    examEndDate:
      examEndDate != null ? "Please add exam end date and time" : undefined,
    examReadingTime: examReadingTime,
    examWritingTime: examWritingTime,
    maxGrade: maxGrade != null ? "Please add total points" : undefined,
  };
};
